/*
 * ET - USP Strip (sections/et-usp-strip.liquid)
 * <et-usp-strip-slider>: the USPs are a static row (desktop) or two-column grid (tablet) from 750px and a Swiper
 * slider below 750px (one USP per slide, bullets in the shared .et-slider-dots element, optional autoplay).
 * The space between slides comes from the section CSS (--et-usp-strip-slide-space, 32px: wider than the 24px gutter, so
 * no pixel of a neighbouring slide shows at the screen edge at rest).
 * Autoplay (off by default) stops for good once the visitor swipes or uses the dots, pauses on mouse hover and while
 * keyboard focus is inside the slider (WCAG 2.2.2), and is disabled for prefers-reduced-motion.
 * Swiper is created/destroyed on the breakpoint change and destroyed on disconnect, so theme editor re-renders
 * (shopify:section:load / unload) never leave a duplicate instance. Blocks selected in the editor slide into view.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-usp-strip-slider')) return;

  const SLIDER_QUERY = '(max-width: 749px)';
  const DEFAULT_SLIDE_SPACE = 32;
  const SLIDE_SELECTOR = '[data-et-usp-strip-slide]';

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class EtUspStripSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.pendingIndex = null;
      this.focusStopped = false;
      this.autoplayInteracted = false;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
    }

    connectedCallback() {
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);

      this.media = window.matchMedia(SLIDER_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleMediaChange, { once: true });
      } else {
        this.handleMediaChange();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);

      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      document.removeEventListener('DOMContentLoaded', this.handleMediaChange);
      this.destroy();
    }

    get autoplayDelay() {
      const delay = parseInt(this.dataset.autoplayDelay || '', 10);
      return delay > 0 && !prefersReducedMotion() ? delay : 0;
    }

    // Space between slides from the section CSS (32px)
    get slideSpace() {
      const space = parseFloat(window.getComputedStyle(this).getPropertyValue('--et-usp-strip-slide-space'));
      return space >= 0 ? space : DEFAULT_SLIDE_SPACE;
    }

    // A theme editor re-render re-inserts the section's stylesheet links, which apply a moment later: wait for them so
    // Swiper never measures (or reads the slide space from) unstyled markup.
    whenStyled() {
      if (window.getComputedStyle(this).getPropertyValue('--et-usp-strip-slide-space').trim() !== '') return Promise.resolve();
      const section = this.closest('.et-usp-strip');
      const pending = section
        ? Array.from(section.querySelectorAll('link[rel="stylesheet"]')).filter((link) => !link.sheet)
        : [];
      return Promise.all(
        pending.map(
          (link) =>
            new Promise((resolve) => {
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            })
        )
      );
    }

    handleMediaChange() {
      if (!this.isConnected) return;
      if (this.media && this.media.matches) this.init();
      else this.destroy();
    }

    init() {
      const swiperEl = this.querySelector('[data-et-usp-strip-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!this.media || !this.media.matches) return;
      if (swiperEl.querySelectorAll(SLIDE_SELECTOR).length < 2) return;

      this.loading = true;
      Promise.all([window.etLoadSwiper(), this.whenStyled()]).then(([Swiper]) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches) return;

        const dotsEl = this.querySelector('[data-et-usp-strip-dots]');
        const list = swiperEl.querySelector('.swiper-wrapper');
        if (list) list.scrollLeft = 0;

        const delay = this.autoplayDelay;
        this.focusStopped = false;
        this.autoplayInteracted = false;

        this.swiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: this.slideSpace,
          speed: 400,
          watchOverflow: true,
          grabCursor: true,
          pagination: dotsEl ? { el: dotsEl, type: 'bullets', clickable: true } : false,
          autoplay: delay ? { delay: delay, disableOnInteraction: true, pauseOnMouseEnter: true } : false,
          a11y: { enabled: true, slideRole: 'listitem' },
          on: {
            // A swipe, drag or dot/keyboard slide change (not autoplay's own) ends autoplay for this visit
            sliderFirstMove: () => {
              this.autoplayInteracted = true;
            },
            beforeTransitionStart: (swiper, speed, internal) => {
              if (!internal) this.autoplayInteracted = true;
            }
          }
        });

        if (this.pendingIndex !== null) {
          this.selectIndex(this.pendingIndex);
          this.pendingIndex = null;
        }
      });
    }

    destroy() {
      this.pendingIndex = null;
      this.focusStopped = false;
      this.autoplayInteracted = false;
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;

      const dotsEl = this.querySelector('[data-et-usp-strip-dots]');
      if (dotsEl) dotsEl.innerHTML = '';

      this.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
        slide.removeAttribute('aria-label');
        slide.removeAttribute('role');
      });

      const list = this.querySelector('.swiper-wrapper');
      if (list) {
        list.removeAttribute('aria-live');
        if (list.id && list.id.indexOf('swiper-wrapper-') === 0) list.removeAttribute('id');
      }
    }

    selectIndex(index) {
      if (!this.swiper) return;
      if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      this.swiper.slideTo(index, 0);
    }

    selectSlide(slide) {
      const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      if (this.swiper) this.selectIndex(index);
      else if (this.media && this.media.matches) this.pendingIndex = index;
    }

    // Theme editor block deselect: restart autoplay even after the editor's own slide change
    resumeAutoplay() {
      this.focusStopped = false;
      this.autoplayInteracted = false;
      if (!this.swiper || !this.autoplayDelay || !this.swiper.autoplay) return;
      if (!this.swiper.autoplay.running) this.swiper.autoplay.start();
    }

    // Keyboard focus inside the slider (a dot or a title link) holds autoplay; it restarts when focus leaves, unless
    // the visitor changed the slide meanwhile. stop/start is used because Swiper resumes a paused autoplay on its own
    // after transitions, pointer leave and tab visibility changes.
    handleFocusIn() {
      const autoplay = this.swiper && this.swiper.autoplay;
      if (!autoplay || !autoplay.running) return;
      this.focusStopped = true;
      autoplay.stop();
    }

    handleFocusOut(event) {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      if (!this.focusStopped) return;
      this.focusStopped = false;
      const autoplay = this.swiper && this.swiper.autoplay;
      if (!autoplay || autoplay.running || this.autoplayInteracted || !this.autoplayDelay) return;
      autoplay.start();
    }
  }

  window.customElements.define('et-usp-strip-slider', EtUspStripSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-usp-strip-slider')) : [];

  const sliderForBlock = (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-usp-strip-slider');
    return slider ? { slide: slide, slider: slider } : null;
  };

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.handleMediaChange()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const match = sliderForBlock(event);
    if (match) match.slider.selectSlide(match.slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const match = sliderForBlock(event);
    if (match) match.slider.resumeAutoplay();
  });
})();
